import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Alert,
  Chip,
  InputAdornment,
  Tooltip,
  IconButton,
  Snackbar,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useAuthStore } from "../../store";
import { validatePhone } from "../../consts";

const UserInfoForm = ({
  user,
  showAlert,
  handleClose,
  setReauthenticateOpen,
}) => {
  const [phoneError, setPhoneError] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const {
    email,
    emailVerified,
    phone,
    lastName,
    name,
    setPhone,
    setLastName,
    setFirstName,
  } = useAuthStore((state) => ({
    email: state.email,
    emailVerified: state.emailVerified,
    phone: state.phone,
    lastName: state.lastName,
    name: state.name,
    setPhone: state.setPhone,
    setLastName: state.setLastName,
    setFirstName: state.setFirstName,
  }));

  const handlePhoneChange = (e) => {
    const phoneValue = e.target.value;
    setPhone(phoneValue);
    if (!validatePhone(phoneValue)) {
      setPhoneError("Nieprawidłowy format numeru telefonu.");
    } else {
      setPhoneError("");
    }
  };

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
  };

  const handleReauthenticateOpen = () => {
    if (phoneError) {
      setSnackbarMessage("Proszę poprawić błędy przed zapisaniem.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    
    if (!name.trim() || !lastName.trim() || !phone.trim()) {
      setSnackbarMessage("Proszę wypełnić wszystkie pola.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setReauthenticateOpen(true);
  };

  return (
    <Box>
      <Typography id="user-modal-title" variant="h6" component="h2" sx={{ mb: 3 }}>
        Moje dane
      </Typography>

      {user && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Email Verification Status */}
          <Paper
            sx={{
              p: 2,
              backgroundColor: emailVerified ? "#E8F5E9" : "#FFF3E0",
              border: `1px solid ${emailVerified ? "#4CAF50" : "#FF9800"}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {emailVerified ? (
                <CheckCircleIcon sx={{ color: "#4CAF50" }} />
              ) : (
                <WarningIcon sx={{ color: "#FF9800" }} />
              )}
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {emailVerified
                  ? "Email zweryfikowany"
                  : "Email niezweryfikowany"}
              </Typography>
            </Box>
          </Paper>

          {/* User Information Card */}
          <Paper
            sx={{
              p: 3,
              backgroundColor: "#FAF3E0",
              border: "1px solid #D2B48C",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Email Field (Read-only) */}
              <Box>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  disabled
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "#5c4033" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Email nie może być zmieniony bezpośrednio. Skontaktuj się z nami, aby zmienić email.">
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                />
                <Alert
                  severity="info"
                  sx={{ mt: 1 }}
                  icon={<InfoIcon fontSize="inherit" />}
                >
                  Aby zmienić email, napisz na{" "}
                  <strong>{process.env.REACT_APP_CONTACT_EMAIL}</strong>
                </Alert>
              </Box>

              {/* Phone Field */}
              <TextField
                label="Telefon"
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                fullWidth
                error={!!phoneError}
                helperText={phoneError}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: "#5c4033" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "#fff",
                  },
                }}
              />

              {/* First Name Field */}
              <TextField
                label="Imię"
                type="text"
                value={name}
                onChange={handleFirstNameChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: "#5c4033" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "#fff",
                  },
                }}
              />

              {/* Last Name Field */}
              <TextField
                label="Nazwisko"
                type="text"
                value={lastName}
                onChange={handleLastNameChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: "#5c4033" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "#fff",
                  },
                }}
              />
            </Box>
          </Paper>

          {/* Action Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleReauthenticateOpen}
              disabled={loading || !!phoneError}
              sx={{
                backgroundColor: "#5c4033",
                padding: "12px 32px",
                fontSize: "15px",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "#4a3329",
                },
                "&:disabled": {
                  backgroundColor: "#ccc",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Zaktualizuj informacje"
              )}
            </Button>
          </Box>
        </Box>
      )}

      {/* Snackbar for error messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserInfoForm;
